import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { TableOfContents, Carousel, PreviewContent, Content, Layout, Metadata, Image } from '../components';
import { media } from '../theme';

export function CustomPageTemplate({ title, baneguide, parent, content, tableOfContents, showTableOfContents, subpages, featuredImage, isPreviewMode }) {
  const PageContent = isPreviewMode ? PreviewContent : Content;

  const isSubpage = (typeof parent === 'string' && parent.length > 0);
  const prependAsSubpage = isSubpage ? '../' : '';

  const subpagesList = !isPreviewMode ? subpages.map((item) => ({
    url: `${prependAsSubpage + item.node.fields.slug.slice(1, -1).split('/')[1]}`,
    title: item.node.frontmatter.title,
  })) : [];

  const hasSubpages = subpagesList.length > 0;

  const hasImage = (typeof featuredImage === 'string' && featuredImage !== '' && !isPreviewMode)
    || featuredImage?.fluid?.src
    || (featuredImage?.path && featuredImage.path.substr(0, 6) === 'static')
    || featuredImage?.url;

  return (
    <>
      <section>
        <div className="container page-content">
          <h1>{title}</h1>

          { hasSubpages && !isPreviewMode && (
            <TableOfContents
              content={subpagesList}
              title="Oversigt"
              ordered={false}
              id="subpages"
              isSubpage={isSubpage}
            />
          )}

          { hasImage && (
            <Image
              className="custom-page-header"
              src={featuredImage}
              style={{ maxHeight: 400, height: 'auto' }}
              innnerStyle={{ maxHeight: 400 }}
              applyCloudinary
              breakpoints={[
                { width: 400, height: 400 },
                { width: 500, height: 400 },
                { width: 600, height: 400 },
                { width: 738, height: 400 },
                { width: 1476, height: 800 },
              ]}
              sizes={`
                ${media.landscapeM} calc((100vw - 40px) * 0.8),
                ${media.desktopS} 738px,
                calc(100vw - 40px - 2px)
              `}
            />
          )}

          {/* { (showTableOfContents || hasSubpages) && !isPreviewMode && ( */}
          { showTableOfContents && tableOfContents.length > 0 && !isPreviewMode && (
            <TableOfContents
              content={tableOfContents}
              title="Indholdsfortegnelse"
              ordered
              id="tableOfContents"
            />
          )}

          <PageContent>{content}</PageContent>
        </div>
        {baneguide.length > 0 && (
          <div className="container page-content">
            <Carousel baneguide={baneguide} isPreviewMode={isPreviewMode} />
          </div>
        )}
      </section>
    </>
  );
}

CustomPageTemplate.propTypes = {
  featuredImage: PropTypes.oneOfType([ PropTypes.object, PropTypes.string ]),
  title: PropTypes.string.isRequired,
  baneguide: PropTypes.arrayOf(
    PropTypes.shape({
      desc: PropTypes.string,
      par: PropTypes.number,
      hcp: PropTypes.number,
      image: PropTypes.oneOfType([ PropTypes.object, PropTypes.string ]),
    }),
  ),
  parent: PropTypes.string,
  tableOfContents: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  content: PropTypes.string,
  isPreviewMode: PropTypes.bool,
  subpages: PropTypes.arrayOf(
    PropTypes.object,
  ),
};

CustomPageTemplate.defaultProps = {
  featuredImage: '',
  baneguide: [],
  parent: '',
  content: '',
  tableOfContents: [],
  isPreviewMode: false,
  subpages: [],
};

const CustomPage = ({ data }) => {
  const { frontmatter, body, excerpt, tableOfContents } = data.mdx;

  const hasImage = frontmatter?.featuredImage;

  return (
    <Layout>
      <Metadata
        title={frontmatter.title}
        image={hasImage ? frontmatter.featuredImage : null}
        description={excerpt}
      />
      <CustomPageTemplate
        title={frontmatter.title}
        featuredImage={frontmatter.featuredImage}
        subpages={data.allMdx.edges}
        parent={frontmatter.parent}
        baneguide={frontmatter.baneguide || []}
        tableOfContents={tableOfContents.items}
        showTableOfContents={frontmatter.showTableOfContents}
        content={body}
      />
    </Layout>
  );
};

CustomPage.propTypes = {
  data: PropTypes.shape({
    mdx: PropTypes.shape({
      frontmatter: PropTypes.object,
      body: PropTypes.string,
      tableOfContents: PropTypes.object,
      showTableOfContents: PropTypes.bool,
    }),
    allMdx: PropTypes.shape({
      edges: PropTypes.array,
    }),
  }).isRequired,
};

export default CustomPage;

export const customPageQuery = graphql`
  query CustomPageTemplate($id: String!, $subSlug: String!) {
    mdx(id: { eq: $id }) {
      body
      tableOfContents
      excerpt(pruneLength: 160)
      frontmatter {
        title
        # featuredImage {
        #   childImageSharp {
        #     fluid(maxWidth: 738, quality: 80) {
        #       ...GatsbyImageSharpFluid_withWebp
        #     }
        #   }
        # }
        featuredImage
        parent
        showTableOfContents
        baneguide {
          desc
          hcp
          par
          # image {
          #   childImageSharp {
          #     fluid {
          #       ...GatsbyImageSharpFluid
          #     }
          #   }
          # }
          image
        }
      }
    }

    allMdx(limit: 50, filter: {frontmatter: {templateKey: {ne: "none"}, hideFromPageGeneration: {ne: true}, parent: {eq: $subSlug}}}) {
      edges {
        node {
          fields {
            slug
          }
          frontmatter {
            title
            parent
          }
        }
      }
    }
  }
`;
